import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  Menu,
  MenuItem,
  InputLabel,
  FormControl,
  TablePagination,
  Snackbar,
  Alert,
  SelectChangeEvent,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { Email, Phone, CheckCircle } from '@mui/icons-material';
import Papa from 'papaparse';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import { lightColors, themeBackgrounds } from '../../theme/theme';
import { decryptResponse, encryptResponse, getSessionKey, validateEmail, validateMobileNumber } from '../../services';
import { UserRole } from '../../features/auth/types';

// Type definitions
interface Member {
  _id?: string;
  id: number;
  name: string;
  email: string;
  mobile: string;
  role: string;
  avatarColor: string;
  user_id?: string;
  organization_id?: string;
  account_status?: string;
  created_at?: string;
}

type RoleType = 'Admin' | 'Billing' | 'Desk Admin' | 'Desk Agent';

interface BulkUploadMember {
  id: string;
  user_full_name: string;
  email_id: string;
  mobile_no: string;
  role: RoleType;
  reason?: string;
}

const DEFAULT_BULK_ROLE: RoleType = 'Desk Agent';
const BULK_AVAILABLE_ROLES: RoleType[] = ['Billing', 'Desk Admin', 'Desk Agent'];

const getCsvField = (row: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  // Case-insensitive fallback
  const lowerMap = Object.keys(row).reduce<Record<string, unknown>>((acc, k) => {
    acc[k.toLowerCase().replace(/\s+/g, '')] = row[k];
    return acc;
  }, {});
  for (const key of keys) {
    const normalized = key.toLowerCase().replace(/\s+/g, '');
    const value = lowerMap[normalized];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
};

export default function Members(): React.ReactElement {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const currentUserRole = userDetails?.role as UserRole | undefined;
  const currentUserId = userDetails?.user_id as string | undefined;
  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [mobile, setMobile] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState<boolean>(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [deletingMember, setDeletingMember] = useState<Member | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuMember, setActionMenuMember] = useState<Member | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Bulk CSV upload state
  const [bulkMembers, setBulkMembers] = useState<BulkUploadMember[]>([]);
  const [openBulkUploadDialog, setOpenBulkUploadDialog] = useState<boolean>(false);
  const [isUploadingBulk, setIsUploadingBulk] = useState<boolean>(false);
  const [csvFileName, setCsvFileName] = useState<string>('');
  const [isBulkRetryMode, setIsBulkRetryMode] = useState<boolean>(false);

  // Validation state
  const [emailError, setEmailError] = useState<string>('');
  const [mobileError, setMobileError] = useState<string>('');
  // In the edit flow the mobile arrives masked (e.g. ******7766). We only
  // validate/send it once the admin has actually typed a new number.
  const [mobileEdited, setMobileEdited] = useState<boolean>(false);

  // Fetch organization members on component mount
  useEffect(() => {
    fetchOrganizationMembers();
  }, [userDetails]);

  const fetchOrganizationMembers = async (): Promise<void> => {
    if (!userDetails?.organization_id) return;

    setIsLoadingMembers(true);
    try {
      const payload = {
        request_id: `get_members_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "get_organization_members",
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          user_id: userDetails.user_id
        }, REQ_ENC_KEY)
      };

      const response = await httpService.post<any>(
        `${API_BASE_URL}/user/getOrganizationMembers`,
        payload
      );

      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<{
          members: any[];
        }>(data, getSessionKey() as string);
        const { members } = decrypted;
        const transformedMembers: Member[] = members.map((apiMember: any, index: number) => ({
          _id: apiMember._id,
          id: index + 1,
          name: apiMember.user_full_name,
          email: apiMember.email_id,
          mobile: apiMember.mobile_no,
          role: apiMember.role,
          avatarColor: lightColors[Math.floor(Math.random() * lightColors.length)],
          user_id: apiMember.user_id,
          organization_id: apiMember.organization_id,
          account_status: apiMember.account_status,
          created_at: apiMember.created_at
        }));

        setMembers(transformedMembers);
      } else {
        toast.error(response.data.message || 'Failed to fetch organization members', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while fetching members. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleAddMemberClick = (): void => setOpenAddDialog(true);

  const handleEditClick = (member: Member): void => {
    if (!canEditMember(member)) {
      toast.error('You are not allowed to update this member.', {
        position: 'top-right',
        autoClose: 3000
      });
      return;
    }

    setEditingMember(member);
    setName(member.name);
    setEmail(member.email);
    setMobile(member.mobile);
    setMobileEdited(false);
    setMobileError('');
    setRole(member.role);
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (member: Member): void => {
    setDeletingMember(member);
    setOpenDeleteDialog(true);
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>, member: Member): void => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuMember(member);
  };

  const handleActionMenuClose = (): void => {
    setActionMenuAnchor(null);
  };

  const handleEditFromMenu = (): void => {
    if (!actionMenuMember) return;
    const member = actionMenuMember;
    handleActionMenuClose();
    handleEditClick(member);
  };

  const handleDeleteFromMenu = (): void => {
    if (!actionMenuMember) return;
    const member = actionMenuMember;
    handleActionMenuClose();
    handleDeleteClick(member);
  };

  const handleDialogClose = (): void => {
    setEmail('');
    setMobile('');
    setRole('');
    setName('');
    setEditingMember(null);
    setDeletingMember(null);
    setOpenAddDialog(false);
    setOpenEditDialog(false);
    setOpenDeleteDialog(false);
    setEmailError('');
    setMobileError('');
    setMobileEdited(false);
  };

  const handleEmailChange = (value: string): void => {
    setEmail(value);
    setEmailError(validateEmail(value));
  };

  const handleMobileChange = (value: string): void => {
    // Strip out any non-digit (mask) characters and cap at 10 digits.
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setMobile(digitsOnly);
    setMobileEdited(true);
    setMobileError(validateMobileNumber(digitsOnly));
  };

  // When the admin focuses a masked mobile field in the edit dialog, clear it
  // so they enter a fresh number instead of typing alongside the mask.
  const handleMobileFocus = (): void => {
    if (!mobileEdited && mobile.includes('*')) {
      setMobile('');
      setMobileEdited(true);
      setMobileError(validateMobileNumber(''));
    }
  };

  const canEditRoles = (): boolean => currentUserRole === 'Admin';

  const canEditMember = (member: Member): boolean => {
    if (currentUserRole === 'Admin') return true;
    if (currentUserRole !== 'Desk Admin') return false;
    if (member.user_id && currentUserId && member.user_id === currentUserId) return true;
    return member.role === 'Billing' || member.role === 'Desk Agent';
  };

  const handleAddMember = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const addMemberPayload = {
        request_id: `add_member_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "add_organization_member",
        data: await encryptResponse({
          organization_id: userDetails?.organization_id || '',
          email_id: email,
          mobile_no: mobile,
          role: role,
          user_full_name: name,
        }, REQ_ENC_KEY)
      };

      type AddMemberResponse = {
        status: number;
        data?: any;
        message?: string;
        [key: string]: any
      };

      const response = await httpService.post<AddMemberResponse>(
        `${API_BASE_URL}/user/addOrganizationMember`,
        addMemberPayload
      );

      console.log("Add Member Response:", response);

      if (response.data.status === 200) {
        toast.success('Member added successfully!', {
          position: 'top-right',
          autoClose: 3000
        });
        // Refresh the members list instead of adding to local state
        await fetchOrganizationMembers();
        handleDialogClose();
        setSnackbarMessage('Organization member added successfully!');
        setSnackbarOpen(true);
      } else {
        toast.error(response.data.message || 'Failed to add member. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while adding the member. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditMember = async (): Promise<void> => {
    if (!editingMember || !userDetails?.organization_id) return;
    if (!canEditMember(editingMember)) {
      toast.error('You are not allowed to update this member.', { position: 'top-right', autoClose: 3000 });
      return;
    }

    // Track only the fields that were actually changed
    const changedFields: any = {};
    const updatePayloadData: any = {
      organization_id: userDetails.organization_id,
      member_id: editingMember.user_id || editingMember._id
    };

    if (editingMember.name !== name) {
      changedFields.name = { old: editingMember.name, new: name };
      updatePayloadData.user_full_name = name;
    }
    if (editingMember.email !== email) {
      changedFields.email = { old: editingMember.email, new: email };
      updatePayloadData.email_id = email;
    }
    // Only touch the mobile number if the admin actually typed a new one.
    // The original value is masked (e.g. ******7766), so we never send it back.
    if (mobileEdited) {
      const mobileValidationError = validateMobileNumber(mobile);
      if (mobileValidationError) {
        setMobileError(mobileValidationError);
        toast.error(mobileValidationError, { position: 'top-right', autoClose: 3000 });
        return;
      }
      changedFields.mobile = { old: editingMember.mobile, new: mobile };
      updatePayloadData.mobile_no = mobile;
    }
    if (canEditRoles() && editingMember.role !== role) {
      changedFields.role = { old: editingMember.role, new: role };
      updatePayloadData.role = role;
    }

    // If no changes, don't make API call
    if (Object.keys(changedFields).length === 0) {
      console.log('No changes made to member details');
      handleDialogClose();
      return;
    }

    console.log('Updated fields:', changedFields);
    setIsLoading(true);

    try {
      const updateMemberPayload = {
        request_id: `update_member_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "update_organization_member",
        data: await encryptResponse(updatePayloadData, REQ_ENC_KEY)
      };

      const response = await httpService.post<any>(
        `${API_BASE_URL}/user/updateOrganizationMember`,
        updateMemberPayload
      );

      if (response.data.status === 200 && response.data.data) {
        await fetchOrganizationMembers();
        handleDialogClose();
        setSnackbarMessage('Member details updated successfully!');
        setSnackbarOpen(true);
        toast.success('Member updated successfully!', {
          position: 'top-right',
          autoClose: 3000
        });
      } else {
        toast.error(response.data.message || 'Failed to update member. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while updating the member. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMember = async (): Promise<void> => {
    if (!deletingMember || !userDetails?.organization_id) return;

    setIsDeleting(true);

    try {
      const deleteMemberPayload = {
        request_id: `remove_member_${Date.now()}`,
        timestamp: new Date().toISOString(),
        action: "remove_organization_member",
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          member_id: deletingMember.user_id || deletingMember._id
        }, REQ_ENC_KEY)
      };

      type RemoveMemberResponse = {
        status: number;
        message?: string;
        data?: any;
        error?: any;
      };

      const response = await httpService.post<RemoveMemberResponse>(
        `${API_BASE_URL}/user/removeOrganizationMember`,
        deleteMemberPayload
      );

      if (response.data.status === 200 && response.data.data) {
        // Refresh the members list
        await fetchOrganizationMembers();
        handleDialogClose();
        setSnackbarMessage('Member removed successfully!');
        setSnackbarOpen(true);
        toast.success('Member removed successfully!', {
          position: 'top-right',
          autoClose: 3000
        });
      } else {
        toast.error(response.data.message || 'Failed to remove member. Please try again.', {
          position: 'top-right',
          autoClose: 3000
        });
      }
    } catch (error) {
      toast.error('An error occurred while removing the member. Please try again.', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number): void => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownloadMembersTemplate = (): void => {
    const csvContent = Papa.unparse({
      fields: ['Name', 'Email Id', 'Mobile No'],
      data: [],
    });
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'organization_members_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleBulkUploadClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleBulkCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a valid CSV file', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setCsvFileName(file.name);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = (results.data as Record<string, unknown>[]) || [];
        if (rows.length === 0) {
          toast.error('CSV file is empty', { position: 'top-right', autoClose: 3000 });
          return;
        }

        const parsed: BulkUploadMember[] = [];
        let skippedCount = 0;

        rows.forEach((row, rowIndex) => {
          const name = getCsvField(row, ['Name', 'user_full_name', 'Full Name']);
          const emailId = getCsvField(row, ['Email Id', 'Email', 'email_id', 'Email ID']);
          const mobileNo = getCsvField(row, ['Mobile No', 'Mobile', 'mobile_no', 'Mobile Number'])
            .replace(/\D/g, '')
            .slice(0, 10);

          if (!name || !emailId || !mobileNo) {
            skippedCount += 1;
            return;
          }

          const emailValidationError = validateEmail(emailId);
          const mobileValidationError = validateMobileNumber(mobileNo);
          if (emailValidationError || mobileValidationError) {
            skippedCount += 1;
            return;
          }

          parsed.push({
            id: `bulk-${Date.now()}-${rowIndex}`,
            user_full_name: name,
            email_id: emailId,
            mobile_no: mobileNo,
            role: DEFAULT_BULK_ROLE,
          });
        });

        if (parsed.length === 0) {
          toast.error(
            'No valid members found. CSV must include Name, Email Id, and Mobile No with valid values.',
            { position: 'top-right', autoClose: 5000 }
          );
          return;
        }

        setBulkMembers(parsed);
        setIsBulkRetryMode(false);
        setOpenBulkUploadDialog(true);

        if (skippedCount > 0) {
          toast.warning(
            `${parsed.length} member(s) loaded. ${skippedCount} row(s) skipped due to missing or invalid fields.`,
            { position: 'top-right', autoClose: 4000 }
          );
        } else {
          toast.success(`${parsed.length} member(s) loaded from CSV.`, {
            position: 'top-right',
            autoClose: 3000,
          });
        }
      },
      error: () => {
        toast.error('Failed to parse CSV file. Please check the file format.', {
          position: 'top-right',
          autoClose: 3000,
        });
      },
    });

    // Allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBulkFieldChange = (
    index: number,
    field: keyof BulkUploadMember,
    value: string
  ): void => {
    setBulkMembers((prev) =>
      prev.map((member, i) => {
        if (i !== index) return member;
        if (field === 'mobile_no') {
          return { ...member, mobile_no: value.replace(/\D/g, '').slice(0, 10), reason: undefined };
        }
        if (field === 'role') {
          return { ...member, role: value as RoleType, reason: undefined };
        }
        if (field === 'id' || field === 'reason') {
          return member;
        }
        return { ...member, [field]: value, reason: undefined };
      })
    );
  };

  const handleRemoveBulkMember = (index: number): void => {
    setBulkMembers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCloseBulkUploadDialog = (): void => {
    setOpenBulkUploadDialog(false);
    setBulkMembers([]);
    setCsvFileName('');
    setIsBulkRetryMode(false);
  };

  const handleUploadOrganizationMembers = async (): Promise<void> => {
    if (!userDetails?.organization_id) {
      toast.error('Organization details are missing. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (bulkMembers.length === 0) {
      toast.error('No members to upload. Please upload a CSV first.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    const invalidRow = bulkMembers.find((member) => {
      const nameMissing = !member.user_full_name.trim();
      const emailError = validateEmail(member.email_id);
      const mobileError = validateMobileNumber(member.mobile_no);
      return nameMissing || !!emailError || !!mobileError;
    });

    if (invalidRow) {
      toast.error('Please fix invalid Name, Email Id, or Mobile No in the preview before uploading.', {
        position: 'top-right',
        autoClose: 4000,
      });
      return;
    }

    setIsUploadingBulk(true);
    try {
      const payload = {
        request_id: `upload_members_${Date.now()}`,
        data: await encryptResponse({
          organization_id: userDetails.organization_id,
          members: bulkMembers.map((member) => ({
            user_full_name: member.user_full_name.trim(),
            email_id: member.email_id.trim(),
            mobile_no: member.mobile_no.trim(),
            role: member.role,
          })),
        }, REQ_ENC_KEY),
      };

      const response = await httpService.post<any>(
        `${API_BASE_URL}/user/uploadOrganizationMembers`,
        payload
      );

      if (response.data?.status === 200 && response.data.data) {
        type UploadMembersResponse = {
          addedMembers?: Array<{
            user_full_name: string;
            email_id: string;
            mobile_no: string;
            role: string;
          }>;
          failedMembers?: Array<{
            user_full_name: string;
            email_id: string;
            mobile_no: string;
            role: string;
            reason?: string;
          }>;
        };

        const responseData = await decryptResponse<UploadMembersResponse>(
          response.data.data,
          getSessionKey() as string
        );
        const addedMembers = responseData?.addedMembers || [];
        const failedMembers = responseData?.failedMembers || [];
        const addedCount = addedMembers.length;
        const failedCount = failedMembers.length;

        if (addedCount > 0) {
          toast.success(
            `${addedCount} member(s) uploaded successfully!`,
            { position: 'top-right', autoClose: 3000 }
          );
          await fetchOrganizationMembers();
          setSnackbarMessage(`${addedCount} organization member(s) uploaded successfully!`);
          setSnackbarOpen(true);
        }

        if (failedCount > 0) {
          const failedRows: BulkUploadMember[] = failedMembers.map((member, index) => ({
            id: `failed-${Date.now()}-${index}`,
            user_full_name: member.user_full_name || '',
            email_id: member.email_id || '',
            mobile_no: (member.mobile_no || '').replace(/\D/g, '').slice(0, 10),
            role: (BULK_AVAILABLE_ROLES.includes(member.role as RoleType)
              ? member.role
              : DEFAULT_BULK_ROLE) as RoleType,
            reason: member.reason || 'Upload failed',
          }));

          setBulkMembers(failedRows);
          setIsBulkRetryMode(true);
          setOpenBulkUploadDialog(true);
          toast.warning(
            `${failedCount} member(s) failed to upload. Please update details and retry.`,
            { position: 'top-right', autoClose: 5000 }
          );
        } else {
          handleCloseBulkUploadDialog();
        }
      } else {
        toast.error(response.data?.message || 'Failed to upload members. Please try again.', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error('An error occurred while uploading members. Please try again.', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsUploadingBulk(false);
    }
  };

  const filteredMembers: Member[] = members.filter(member =>
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedMembers: Member[] = filteredMembers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const roles: RoleType[] = ["Admin", "Billing", "Desk Admin", "Desk Agent"];
  const availableRoles: RoleType[] = ["Billing", "Desk Admin", "Desk Agent"];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <PageHeader
          heading="Organization Members"
          subheading="Manage your organization members and their roles"
          variant="gradient"
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <TextField
          placeholder="Search"
          size="small"
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          InputProps={{ endAdornment: <IconButton><SearchIcon /></IconButton> }}
        />
        <Button
          variant="outlined"
          color="primary"
          size="small"
          startIcon={<PersonAddIcon />}
          onClick={handleAddMemberClick}
        >
          Add Member
        </Button>
        <Tooltip title="Download CSV template">
          <IconButton
            color="primary"
            size="small"
            onClick={handleDownloadMembersTemplate}
            aria-label="Download members CSV template"
          >
            <DownloadIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="contained"
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={handleBulkUploadClick}
          disabled={isUploadingBulk}
          sx={{
            background: isUploadingBulk ? 'grey' : themeBackgrounds.default,
            color: '#ffffff',
            '&:hover': {
              background: themeBackgrounds.default,
              opacity: 0.9,
            },
          }}
        >
          Upload CSV
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleBulkCsvFileChange}
        />
      </Box>

      <Paper>
        {isLoadingMembers ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading members...</Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No members found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMembers.map((member) => (
                    <TableRow key={member._id || member.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ bgcolor: member.avatarColor }}>
                            {member.name.charAt(0)}
                          </Avatar>
                          {member.name}
                        </Box>
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.mobile}</TableCell>
                      <TableCell>{member.role}</TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            bgcolor: member.account_status === 'Active' ? '#e8f5e8' : '#fff3cd',
                            color: member.account_status === 'Active' ? '#2e7d32' : '#856404'
                          }}
                        >
                          {member.account_status || 'Active'}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={(event) => handleActionMenuOpen(event, member)}
                          aria-label="Member actions"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredMembers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={handleEditFromMenu}
          disabled={!actionMenuMember || !canEditMember(actionMenuMember)}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        {actionMenuMember?.role !== 'Admin' && (
          <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Add Member Dialog */}
      <Dialog open={openAddDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Add Organization Member
          <IconButton onClick={handleDialogClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add a new member directly to your organization.
          </Typography>

          <TextField
            fullWidth
            label="Full Name"
            placeholder="Enter full name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Email"
            placeholder="Enter email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleEmailChange(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Mobile Number"
            placeholder="Enter mobile number"
            type="number"
            value={mobile}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleMobileChange(e.target.value)}
            error={!!mobileError}
            helperText={mobileError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
              endAdornment: mobile && !validateMobileNumber(mobile) ? (
                <InputAdornment position="end">
                  <CheckCircle sx={{ color: 'green' }} />
                </InputAdornment>
              ) : null,
            }}
            sx={{ mb: 3 }}
          />

          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={role}
              onChange={(e: SelectChangeEvent) => setRole(e.target.value)}
              label="Role"
            >
              {availableRoles.map((roleOption) => (
                <MenuItem key={roleOption} value={roleOption}>
                  <Box display="flex" justifyContent="space-between" width="100%">
                    {roleOption}
                    <Box component="span" sx={{ fontSize: '10px', bgcolor: '#eee', px: 1, borderRadius: '4px' }}>SYSTEM ROLE</Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              !name ||
              !email ||
              !mobile ||
              !role ||
              isLoading ||
              !!emailError ||
              !!mobileError
            }
            onClick={handleAddMember}
            sx={{
              background: (!name || !email || !mobile || !role || isLoading || !!emailError || !!mobileError) ? 'grey' : themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
            }}
          >
            {isLoading ? 'Adding...' : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={openEditDialog} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Member Details
          <IconButton onClick={handleDialogClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Update member information and role.
          </Typography>

          <TextField
            fullWidth
            label="Full Name"
            placeholder="Enter full name"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Mobile Number"
            placeholder="Enter mobile number"
            value={mobile}
            onFocus={handleMobileFocus}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleMobileChange(e.target.value)}
            error={mobileEdited && !!mobileError}
            helperText={mobileEdited ? mobileError : 'Tap to enter a new number, or leave it to keep the current one'}
            inputProps={{ inputMode: 'numeric', maxLength: 10 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Phone />
                </InputAdornment>
              ),
              endAdornment: mobileEdited && mobile && !validateMobileNumber(mobile) ? (
                <InputAdornment position="end">
                  <CheckCircle sx={{ color: 'green' }} />
                </InputAdornment>
              ) : null,
            }}
            sx={{ mb: 3 }}
          />

          {role !== 'Admin' && canEditRoles() && <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={role}
              onChange={(e: SelectChangeEvent) => setRole(e.target.value)}
              label="Role"
            >
              {availableRoles.map((roleOption) => (
                <MenuItem key={roleOption} value={roleOption}>
                  <Box display="flex" justifyContent="space-between" width="100%">
                    {roleOption}
                    <Box component="span" sx={{ fontSize: '10px', bgcolor: '#eee', px: 1, borderRadius: '4px' }}>SYSTEM ROLE</Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isLoading}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!name || !role || isLoading || (mobileEdited && !!mobileError)}
            onClick={handleEditMember}
          >
            {isLoading ? 'Updating...' : 'Update Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Member Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Remove Member
          <IconButton onClick={handleDialogClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to remove <strong>{deletingMember?.name}</strong> from the organization?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The member will lose access to all organization resources.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteMember}
            disabled={isDeleting}
          >
            {isDeleting ? 'Removing...' : 'Remove Member'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk CSV Upload Review Dialog */}
      <Dialog
        open={openBulkUploadDialog}
        onClose={isUploadingBulk ? undefined : handleCloseBulkUploadDialog}
        maxWidth={isBulkRetryMode ? 'lg' : 'md'}
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {isBulkRetryMode ? 'Failed Members - Update & Retry' : 'Review Uploaded Members'}
          <IconButton onClick={handleCloseBulkUploadDialog} disabled={isUploadingBulk}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isBulkRetryMode ? (
              <>
                These members failed to upload. Review the reason, update Name, Email Id, Mobile No, or Role, then retry upload.
              </>
            ) : csvFileName ? (
              <>Review and edit members from <strong>{csvFileName}</strong>. Update Name, Email Id, Mobile No, and Role, then upload.</>
            ) : (
              'Review and edit members, then assign roles before uploading.'
            )}
            {' '}Default role is <strong>Desk Agent</strong>.
          </Typography>

          {isBulkRetryMode && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {bulkMembers.length} member(s) failed. Fix the details below and upload again.
            </Alert>
          )}

          {bulkMembers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No members to upload.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>Email Id</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>Mobile No</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Role</TableCell>
                  {isBulkRetryMode && (
                    <TableCell sx={{ fontWeight: 700, minWidth: 220 }}>Reason</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700 }} align="right">Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bulkMembers.map((member, index) => {
                  const emailFieldError = validateEmail(member.email_id);
                  const mobileFieldError = validateMobileNumber(member.mobile_no);
                  const nameFieldError = !member.user_full_name.trim() ? 'Name is required' : '';

                  return (
                    <TableRow key={member.id}>
                      <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={member.user_full_name}
                          onChange={(e) => handleBulkFieldChange(index, 'user_full_name', e.target.value)}
                          error={!!nameFieldError}
                          helperText={nameFieldError || ' '}
                          FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                          disabled={isUploadingBulk}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={member.email_id}
                          onChange={(e) => handleBulkFieldChange(index, 'email_id', e.target.value)}
                          error={!!emailFieldError}
                          helperText={emailFieldError || ' '}
                          FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                          disabled={isUploadingBulk}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                        <TextField
                          size="small"
                          fullWidth
                          value={member.mobile_no}
                          onChange={(e) => handleBulkFieldChange(index, 'mobile_no', e.target.value)}
                          error={!!mobileFieldError}
                          helperText={mobileFieldError || ' '}
                          FormHelperTextProps={{ sx: { minHeight: 20, m: 0, mt: 0.5 } }}
                          inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                          disabled={isUploadingBulk}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                        <FormControl size="small" fullWidth error={false}>
                          <Select
                            value={member.role}
                            onChange={(e: SelectChangeEvent) =>
                              handleBulkFieldChange(index, 'role', e.target.value)
                            }
                            disabled={isUploadingBulk}
                          >
                            {BULK_AVAILABLE_ROLES.map((roleOption) => (
                              <MenuItem key={roleOption} value={roleOption}>
                                {roleOption}
                              </MenuItem>
                            ))}
                          </Select>
                          <Typography variant="caption" sx={{ minHeight: 20, mt: 0.5, display: 'block', visibility: 'hidden' }}>
                            spacer
                          </Typography>
                        </FormControl>
                      </TableCell>
                      {isBulkRetryMode && (
                        <TableCell sx={{ verticalAlign: 'top', py: 1.5 }}>
                          <Typography
                            variant="body2"
                            color={member.reason ? 'error' : 'success.main'}
                            sx={{ pt: 1, lineHeight: 1.4 }}
                          >
                            {member.reason || 'Ready to retry'}
                          </Typography>
                          <Typography variant="caption" sx={{ minHeight: 20, mt: 0.5, display: 'block', visibility: 'hidden' }}>
                            spacer
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ verticalAlign: 'top', py: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start', pt: 0.5 }}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveBulkMember(index)}
                            disabled={isUploadingBulk}
                            aria-label="Remove member from upload list"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkUploadDialog} disabled={isUploadingBulk}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadOrganizationMembers}
            disabled={bulkMembers.length === 0 || isUploadingBulk}
            startIcon={isUploadingBulk ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
            sx={{
              background: (bulkMembers.length === 0 || isUploadingBulk) ? 'grey' : themeBackgrounds.default,
              color: '#ffffff',
              '&:hover': {
                background: themeBackgrounds.default,
                opacity: 0.9,
              },
            }}
          >
            {isUploadingBulk
              ? 'Uploading...'
              : isBulkRetryMode
                ? `Retry Upload (${bulkMembers.length})`
                : `Upload ${bulkMembers.length} Member${bulkMembers.length === 1 ? '' : 's'}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}